import React, { useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, Chip, Divider,
  FormControlLabel, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { providersApi } from '../services/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

/**
 * Admin view + actions for one provider's Pro entitlement (spec §6). Only SERVICE providers get
 * grant/revoke; STORE/INFLUENCER get the override actions only. Error bodies are shown verbatim.
 */
export default function ProviderSubscriptionCard({ provider, onChanged }) {
  const sub = provider.subscription;
  const isService = (provider.providerType || 'SERVICE') === 'SERVICE';
  const isGrant = sub?.channel === 'ADMIN' && sub.status === 'ACTIVE';
  const isPaidLive = sub && sub.channel !== 'ADMIN' && ['ACTIVE', 'GRACE', 'CANCELED'].includes(sub.status);
  const overrideOff = provider.proOverride === 'OFF';

  const [mode, setMode] = useState(null); // null | 'GRANT' | 'CHANGE_END'
  const [untilDate, setUntilDate] = useState(sub?.untilDate || '');
  const [reason, setReason] = useState('');
  const [clearOverride, setClearOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const call = async (payload) => {
    setBusy(true); setError('');
    try {
      await providersApi.proPlanAction(provider.id, payload);
      setMode(null); setReason(''); setClearOverride(false);
      await onChanged();
    } catch (e) {
      // apiRequest is fetch-based: a plain Error with .backendMessage (the server's reason code).
      setError(e.backendMessage || e.message || 'Request failed');
    } finally { setBusy(false); }
  };

  const submitDated = () => {
    if (mode === 'GRANT') {
      const payload = { action: 'GRANT', reason, untilDate };
      if (overrideOff && clearOverride) payload.clearOverride = true;
      return call(payload);
    }
    const shortens = sub?.untilDate && untilDate < sub.untilDate;
    if (shortens && !window.confirm(`This moves the end EARLIER (from ${sub.untilDate} to ${untilDate}). Continue?`)) return undefined;
    const payload = { action: 'CHANGE_END', reason, untilDate };
    if (shortens) payload.allowShorten = true;
    return call(payload);
  };

  const revoke = () => {
    const r = window.prompt('Reason for revoking');
    if (r) call({ action: 'REVOKE', reason: r });
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6">Subscription</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
        <Chip label={provider.isProPlan ? 'PRO' : 'FREE'} color={provider.isProPlan ? 'success' : 'default'} size="small" />
        {provider.proOverride && <Chip label={`override ${provider.proOverride}`} color="warning" size="small" />}
        {sub ? (
          <Typography variant="body2">
            {sub.channel} · {sub.status} · {isGrant ? `Pro until ${sub.untilDate}` : `period end ${fmt(sub.currentPeriodEnd)}`}
            {sub.graceDeadlineAt ? ` · grace until ${fmt(sub.graceDeadlineAt)}` : ''}
            {sub.grantedBy ? ` · granted by ${sub.grantedBy}` : ''}
            {` · source ${sub.periodSource}`}
          </Typography>
        ) : <Typography variant="body2" color="text.secondary">No subscription</Typography>}
      </Box>
      {provider.checkout && (
        <Alert severity={provider.checkout.state === 'NEEDS_REVIEW' ? 'error' : 'info'} sx={{ mt: 1 }}>
          Checkout {provider.checkout.state} · request {provider.checkout.requestId}{provider.checkout.externalId ? ` · ${provider.checkout.externalId}` : ''}
        </Alert>
      )}
      {provider.cancelOutbox?.length > 0 && <Alert severity="warning" sx={{ mt: 1 }}>{provider.cancelOutbox.length} remote cancel(s) pending</Alert>}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      {isService && !isPaidLive && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {isGrant ? (
            <>
              <Button variant="outlined" onClick={() => { setMode('CHANGE_END'); setUntilDate(sub.untilDate); }}>Change end date</Button>
              <Button variant="outlined" color="error" disabled={busy} onClick={revoke}>Revoke</Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setMode('GRANT')}>Grant Pro</Button>
          )}
        </Stack>
      )}

      {mode && (
        <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {mode === 'GRANT' && overrideOff && (
            <>
              <Alert severity="warning" sx={{ mb: 1 }}>This provider&apos;s Pro override is OFF. A grant has no effect unless the override is cleared.</Alert>
              <FormControlLabel control={<Checkbox checked={clearOverride} onChange={(e) => setClearOverride(e.target.checked)} />} label="Clear the OFF override and grant" />
            </>
          )}
          <Stack spacing={2}>
            <TextField label="Until (inclusive, Israel time)" type="date" value={untilDate} onChange={(e) => setUntilDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} required />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disabled={busy || !untilDate || !reason || (mode === 'GRANT' && overrideOff && !clearOverride)} onClick={submitDated}>
                {mode === 'GRANT' ? 'Confirm grant' : 'Confirm change'}
              </Button>
              <Button onClick={() => setMode(null)}>Cancel</Button>
            </Stack>
          </Stack>
        </Box>
      )}

      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>Advanced</Typography></AccordionSummary>
        <AccordionDetails>
          <Alert severity="warning" sx={{ mb: 1 }}>Overrides bypass billing. ON forces Pro, OFF blocks Pro AND purchases. Clear returns to subscription-derived.</Alert>
          <TextField label="Override reason" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} fullWidth sx={{ mb: 1 }} />
          <Stack direction="row" spacing={1}>
            {isService && <Button disabled={busy || !overrideReason} onClick={() => call({ action: 'OVERRIDE_ON', reason: overrideReason })}>Override ON</Button>}
            <Button disabled={busy || !overrideReason} color="error" onClick={() => call({ action: 'OVERRIDE_OFF', reason: overrideReason })}>Override OFF</Button>
            <Button disabled={busy || !overrideReason} onClick={() => call({ action: 'OVERRIDE_CLEAR', reason: overrideReason })}>Clear override</Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography>History, audit &amp; transactions</Typography></AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2">Subscriptions</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Channel</TableCell><TableCell>Status</TableCell><TableCell>Period end</TableCell><TableCell>Source</TableCell><TableCell>Created</TableCell></TableRow></TableHead>
            <TableBody>{(provider.subscriptionHistory || []).map((s) => <TableRow key={s.id}><TableCell>{s.channel}</TableCell><TableCell>{s.status}</TableCell><TableCell>{fmt(s.currentPeriodEnd)}</TableCell><TableCell>{s.periodSource}</TableCell><TableCell>{fmt(s.createdAt)}</TableCell></TableRow>)}</TableBody>
          </Table>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">Audit</Typography>
          <Table size="small">
            <TableBody>{(provider.proAudit || []).map((a, i) => <TableRow key={i}><TableCell>{fmt(a.createdAt)}</TableCell><TableCell>{a.action}</TableCell><TableCell>{a.actorId}</TableCell><TableCell>{a.reason}</TableCell><TableCell>{a.until ? fmt(a.until) : ''}{a.previousUntil ? ` (was ${fmt(a.previousUntil)})` : ''}{a.details || ''}</TableCell></TableRow>)}</TableBody>
          </Table>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2">Transactions</Typography>
          <Table size="small">
            <TableBody>{(provider.transactions || []).map((t) => <TableRow key={t.transactionId}><TableCell>{fmt(t.occurredAt)}</TableCell><TableCell>{t.transactionId}</TableCell><TableCell>{t.status}</TableCell><TableCell>{(t.amountCents / 100).toFixed(2)} {t.currency}</TableCell></TableRow>)}</TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
